import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  reviewApi,
  ReviewApiError,
  type MyPickReviewDraft,
  type PickVerdict,
  type ReviewDisplayNameMode,
} from '../../services/reviewApi';
import Sheet from '../ui/Sheet';
import { btnPrimary, focusRing } from '../ui/redesign';

const MIN = 10;
const MAX = 500;

/**
 * Write or edit a review of a YOYO Pick (PRD §5.5, prototype `reviewSheet`):
 * recommend or not, a reason of 10 to 500 characters, "we went" and a byline.
 * Reviews are published after moderation.
 */
export default function PickReviewSheet({
  open,
  pickId,
  pickType,
  existing,
  onClose,
  onSaved,
}: {
  open: boolean;
  pickId: string;
  pickType: 'PROVIDER' | 'EVENT';
  existing: (MyPickReviewDraft & { id: string }) | null;
  onClose: () => void;
  onSaved: (kind: 'created' | 'updated') => void;
}) {
  const { t } = useTranslation(['picks', 'review', 'common']);
  const [verdict, setVerdict] = useState<PickVerdict>('RECOMMEND');
  const [content, setContent] = useState('');
  const [visited, setVisited] = useState(true);
  const [mode, setMode] = useState<ReviewDisplayNameMode>('account');
  const [customName, setCustomName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Opening the sheet starts from the saved review, or a fresh one.
  useEffect(() => {
    if (!open) return;
    setVerdict(existing?.verdict ?? 'RECOMMEND');
    setContent(existing?.content ?? '');
    setVisited(existing ? existing.visited : true);
    setMode(existing?.displayNameMode ?? 'account');
    setCustomName(existing?.displayNameMode === 'custom' ? (existing.authorDisplayName ?? '') : '');
    setError(null);
  }, [open, existing]);

  const length = content.trim().length;
  const valid = length >= MIN && length <= MAX && (mode !== 'custom' || customName.trim() !== '');

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    const payload = {
      verdict,
      content: content.trim(),
      visited,
      displayNameMode: mode,
      ...(mode === 'custom' ? { customName: customName.trim() } : {}),
    };
    try {
      if (existing) await reviewApi.pickUpdate(existing.id, payload);
      else await reviewApi.pickCreate(pickId, payload);
      onSaved(existing ? 'updated' : 'created');
    } catch (err) {
      setError(err instanceof ReviewApiError ? err.message : t('picks:review.failed'));
    } finally {
      setBusy(false);
    }
  };

  const verdictButton = (value: PickVerdict) => {
    const up = value === 'RECOMMEND';
    const on = verdict === value;
    return (
      <button
        type="button"
        aria-pressed={on}
        onClick={() => setVerdict(value)}
        className={`flex min-h-[48px] items-center justify-center gap-2 rounded-[14px] border-[1.5px] p-3 font-ui font-semibold ${focusRing} ${
          on
            ? up
              ? 'border-primary bg-primary-pale text-primary-dark'
              : 'border-brown bg-[#F1E7DF] text-brown'
            : 'border-line bg-white text-ink'
        }`}
      >
        {up ? (
          <ThumbsUp className="h-[18px] w-[18px]" aria-hidden="true" />
        ) : (
          <ThumbsDown className="h-[18px] w-[18px]" aria-hidden="true" />
        )}
        {up ? t('picks:detail.recommend') : t('picks:detail.notRecommend')}
      </button>
    );
  };

  const radio = (value: ReviewDisplayNameMode, label: string) => (
    <label
      className={`inline-flex min-h-[44px] cursor-pointer items-center gap-[7px] rounded-[12px] border-[1.5px] px-[13px] py-[9px] text-sm font-medium ${
        mode === value ? 'border-primary bg-primary-pale' : 'border-line bg-white'
      }`}
    >
      <input
        type="radio"
        name="pick-review-byline"
        checked={mode === value}
        onChange={() => setMode(value)}
        className="accent-primary"
      />
      {label}
    </label>
  );

  return (
    <Sheet
      open={open}
      onClose={onClose}
      closeLabel={t('common:share.close')}
      title={existing ? t('picks:review.editTitle') : t('picks:review.title')}
    >
      <p className="mt-1.5 text-[14.5px] text-ink-2">{t('picks:review.moderated')}</p>
      <div role="group" className="mt-3.5 grid grid-cols-2 gap-2.5">
        {verdictButton('RECOMMEND')}
        {verdictButton('NOT_RECOMMEND')}
      </div>

      <div className="mt-3.5 flex flex-col gap-1.5">
        <label htmlFor="pick-review-text" className="font-ui text-[13px] font-semibold">
          {t('picks:review.reason')}
        </label>
        <textarea
          id="pick-review-text"
          value={content}
          maxLength={MAX}
          onChange={(e) => setContent(e.target.value)}
          placeholder={t('picks:review.placeholder')}
          className="min-h-[110px] w-full resize-y rounded-[12px] border-[1.5px] border-line bg-white px-3 py-[11px] text-[15px] leading-[1.6] outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(58,125,68,0.12)]"
        />
        <span className="text-[12.5px] text-mute">
          {t('picks:review.lengthHint', { count: length })}
        </span>
        <span className="text-[12.5px] text-mute">{t('picks:review.piiHint')}</span>
      </div>

      <label className="mt-3 inline-flex min-h-[44px] cursor-pointer items-center gap-[7px] rounded-[12px] border-[1.5px] border-line bg-white px-[13px] py-[9px] text-sm font-medium has-[:checked]:border-primary has-[:checked]:bg-primary-pale">
        <input
          type="checkbox"
          checked={visited}
          onChange={(e) => setVisited(e.target.checked)}
          className="accent-primary"
        />
        {/* Already ticked: the wording stays the one it was ticked with (P1). */}
        {t(
          `picks:visited.${existing?.visited && existing.visitedAs ? existing.visitedAs : pickType}`
        )}
      </label>

      <div className="mt-3 flex flex-col gap-1.5">
        <span className="font-ui text-[13px] font-semibold">{t('picks:review.byline')}</span>
        <div className="flex flex-wrap gap-2">
          {radio('account', t('review:editor.nameAccount'))}
          {radio('custom', t('review:editor.nameCustom'))}
          {radio('anonymous', t('review:editor.nameAnonymous'))}
        </div>
        {mode === 'custom' && (
          <input
            value={customName}
            maxLength={40}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder={t('review:editor.customNamePlaceholder')}
            className="min-h-[44px] w-full rounded-[12px] border-[1.5px] border-line bg-white px-3 py-[11px] text-[15px] outline-none focus:border-primary"
          />
        )}
      </div>

      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}

      <div className="mt-[18px] flex">
        <button
          type="button"
          className={`${btnPrimary} flex-1`}
          disabled={!valid || busy}
          onClick={submit}
        >
          {existing ? t('review:editor.submitUpdate') : t('review:editor.submitCreate')}
        </button>
      </div>
    </Sheet>
  );
}
